<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Card, Table, TableHead, TableHeadCell, TableBody, TableBodyRow, TableBodyCell,
		Button, Modal, Label, Input, Select, Toggle, Spinner, Badge
	} from 'flowbite-svelte';
	import PageHeader from '../../components/PageHeader.svelte';
	import RowAction from '../../components/admin/RowAction.svelte';
	import {
		listAlertChannels, createAlertChannel, updateAlertChannel, deleteAlertChannel, testAlertChannel
	} from '../../AppNetwork';
	import type { AlertChannel } from '../../AppTypes';

	let channels: AlertChannel[] = [];
	let loading = true;
	let error = '';
	let message = '';

	let showModal = false;
	let editing: AlertChannel | null = null;
	let form = blankForm();
	let isSaving = false;

	function blankForm() {
		return { name: '', type: 'email' as 'email' | 'teams_webhook', target: '', enabled: true };
	}

	async function loadChannels() {
		loading = true;
		error = '';
		try {
			channels = await listAlertChannels();
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to load channels';
		} finally {
			loading = false;
		}
	}
	onMount(loadChannels);

	function openCreate() {
		editing = null; form = blankForm(); error = ''; showModal = true;
	}
	function openEdit(channel: AlertChannel) {
		editing = channel; error = '';
		form = { name: channel.name, type: channel.type, target: channel.target, enabled: channel.enabled };
		showModal = true;
	}

	async function save() {
		isSaving = true;
		error = '';
		try {
			if (editing) await updateAlertChannel(editing.id, form);
			else await createAlertChannel(form);
			showModal = false;
			await loadChannels();
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to save channel';
		}
		isSaving = false;
	}

	async function remove(channel: AlertChannel) {
		if (!confirm(`Delete channel "${channel.name}"?`)) return;
		await deleteAlertChannel(channel.id);
		await loadChannels();
	}

	async function test(channel: AlertChannel) {
		message = `Testing ${channel.name}…`;
		try {
			const result = await testAlertChannel(channel.id);
			message = result.success ? `Test delivered to ${channel.name}.` : `Test to ${channel.name} failed to deliver.`;
		} catch (e) {
			message = e instanceof Error ? e.message : 'Test failed';
		}
	}
</script>

<PageHeader title="Alert Channels" description="Where alerts are delivered when a service crosses a threshold." />

<div class="space-y-6">
	<Card class="max-w-none">
		<div class="flex items-center justify-between gap-4">
			<p class="text-sm text-gray-500 dark:text-gray-400">
				{#if message}<i class="fa-solid fa-circle-info me-2"></i>{message}{/if}
			</p>
			<Button on:click={openCreate}>
				<i class="fa-solid fa-plus me-2"></i>New Channel
			</Button>
		</div>
	</Card>

	<Card padding="none" class="max-w-none">
		{#if loading}
			<div class="p-8 text-center">
				<i class="fa-solid fa-spinner fa-spin text-2xl text-gray-400 mb-2"></i>
				<p class="text-gray-500 dark:text-gray-400">Loading channels...</p>
			</div>
		{:else if error}
			<div class="p-8 text-center">
				<i class="fa-solid fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
				<p class="text-gray-500 dark:text-gray-400">{error}</p>
				<Button class="mt-4" on:click={loadChannels}>Retry</Button>
			</div>
		{:else if channels.length === 0}
			<div class="p-8 text-center">
				<i class="fa-solid fa-bell text-4xl text-gray-300 dark:text-gray-600 mb-4"></i>
				<p class="text-gray-500 dark:text-gray-400">No alert channels configured</p>
			</div>
		{:else}
			<Table striped divClass="relative overflow-x-auto rounded-lg">
				<TableHead class="bg-gray-50 dark:bg-gray-700">
					<TableHeadCell>Name</TableHeadCell>
					<TableHeadCell>Type</TableHeadCell>
					<TableHeadCell>Target</TableHeadCell>
					<TableHeadCell>Enabled</TableHeadCell>
					<TableHeadCell>Actions</TableHeadCell>
				</TableHead>
				<TableBody>
					{#each channels as channel}
						<TableBodyRow>
							<TableBodyCell class="font-medium text-gray-900 dark:text-white">{channel.name}</TableBodyCell>
							<TableBodyCell class="text-gray-500 dark:text-gray-400">{channel.type === 'email' ? 'Email' : 'Teams webhook'}</TableBodyCell>
							<TableBodyCell class="max-w-xs truncate text-gray-500 dark:text-gray-400">{channel.target}</TableBodyCell>
							<TableBodyCell>
								{#if channel.enabled}<Badge color="green">On</Badge>{:else}<Badge color="dark">Off</Badge>{/if}
							</TableBodyCell>
							<TableBodyCell>
								<div class="flex gap-1">
									<RowAction icon="fa-paper-plane" label="Send test" on:click={() => test(channel)} />
									<RowAction icon="fa-pen" label="Edit" on:click={() => openEdit(channel)} />
									<RowAction icon="fa-trash" label="Delete" danger on:click={() => remove(channel)} />
								</div>
							</TableBodyCell>
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>
		{/if}
	</Card>
</div>

<Modal bind:open={showModal} title={editing ? 'Edit Channel' : 'New Channel'} size="md">
	<div class="space-y-4">
		<div><Label class="mb-1">Name</Label><Input bind:value={form.name} /></div>
		<div><Label class="mb-1">Type</Label>
			<Select bind:value={form.type}>
				<option value="email">Email</option>
				<option value="teams_webhook">Microsoft Teams webhook</option>
			</Select>
		</div>
		<div><Label class="mb-1">{form.type === 'email' ? 'Email address' : 'Webhook URL'}</Label><Input bind:value={form.target} /></div>
		<Toggle bind:checked={form.enabled}>Enabled</Toggle>
		{#if error}<p class="text-red-500 text-sm">{error}</p>{/if}
	</div>
	<svelte:fragment slot="footer">
		<div class="flex gap-2">
			<Button color="primary" on:click={save} disabled={isSaving || !form.name || !form.target}>
				{#if isSaving}<Spinner size="sm" class="mr-2" />{/if}Save
			</Button>
			<Button color="alternative" on:click={() => (showModal = false)}>Cancel</Button>
		</div>
	</svelte:fragment>
</Modal>
